"use client";

import { AlertCircle, CheckCircle, Check } from "lucide-react";
import { useState, useEffect, type Ref } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/ui/popover";
import { cn } from "@/lib/utils";

export type ValidationErrorT = {
  message: string;
  isValidated: boolean;
};

export type ValidationPopoverBgT =
  | "background"
  | "card"
  | "input"
  | "accent"
  | "muted";

type ValidationPopoverPropsT = {
  error?:
    | string
    | string[]
    | ValidationErrorT[]
    | (string | ValidationErrorT)[];
  className?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerRef?:
    | Ref<HTMLInputElement | null>
    | Ref<HTMLTextAreaElement | null>
    | Ref<HTMLButtonElement | null>
    | null;
  bgVariant?: ValidationPopoverBgT;
  children?: React.ReactNode; // Für Input als Trigger
  triggerOnHover?: boolean; // New prop for hover trigger
  sideOffset?: number;
};

export default function ValidationPopover({
  error,
  className = "",
  isOpen,
  onOpenChange,
  triggerRef,
  bgVariant = "muted",
  children,
  triggerOnHover = false,
  sideOffset = 4,
}: ValidationPopoverPropsT) {
  const [isMounted, setIsMounted] = useState(false);

  // Avoid SSR/CSR id drift from Radix popover by rendering only after mount
  useEffect(() => setIsMounted(true), []);

  // Handle different error types and convert to ValidationErrorT[]
  const getValidationErrors = (): ValidationErrorT[] => {
    if (!error) return [];

    if (Array.isArray(error)) {
      return error.map((err) => {
        if (typeof err === "string") {
          return { message: err, isValidated: false };
        } else if (typeof err === "object" && "message" in err) {
          return err as ValidationErrorT;
        }
        return { message: String(err), isValidated: false };
      });
    }

    return [{ message: String(error), isValidated: false }];
  };

  const errors = getValidationErrors();
  const hasErrors = errors.length > 0;
  const allValidated = errors.every((error) => error.isValidated);

  // Controlled state for popover
  const [open, setOpen] = useState(false);
  const isControlled = isOpen !== undefined;
  const currentOpen = isControlled ? isOpen : open;
  const handleOpenChange = isControlled ? onOpenChange : setOpen;

  // Auto-close when errors are resolved (only for uncontrolled mode)
  useEffect(() => {
    if (!hasErrors && !isControlled && open) {
      setOpen(false);
    }
  }, [hasErrors, isControlled, open]);

  // Handle popover open to maintain input focus
  const handleOpenChangeWithFocus = (newOpen: boolean) => {
    handleOpenChange?.(newOpen);
    // Removed automatic focus to prevent popover from closing immediately
  };

  // Handle hover events for image components
  const handleMouseEnter = () => {
    if (triggerOnHover && hasErrors && !isControlled) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (triggerOnHover && hasErrors && !isControlled) {
      setOpen(false);
    }
  };

  // Get background class based on variant
  const getBackgroundClass = () => {
    switch (bgVariant) {
      case "background":
        return "bg-background";
      case "input":
        return "bg-input";
      case "accent":
        return "bg-accent";
      case "muted":
        return "bg-muted";
      case "card":
      default:
        return "bg-card";
    }
  };

  // Get arrow border class based on variant
  const getArrowBorderClass = () => {
    switch (bgVariant) {
      case "background":
        return "border-t-background";
      case "input":
        return "border-t-input";
      case "accent":
        return "border-t-accent";
      case "muted":
        return "border-t-muted";
      case "card":
      default:
        return "border-t-card";
    }
  };

  if (!isMounted) {
    // Render raw children (or nothing) on the server to keep markup stable
    return children ? <>{children}</> : null;
  }

  if (!hasErrors) {
    return children ? <>{children}</> : null;
  }

  return (
    <Popover
      open={hasErrors ? currentOpen : false}
      onOpenChange={hasErrors ? handleOpenChangeWithFocus : undefined}
    >
      <PopoverTrigger asChild>
        {children ? (
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {children}
            {/* Icons entfernt - Input ist der Trigger */}
          </div>
        ) : hasErrors ? (
          allValidated ? (
            <CheckCircle
              className={`absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 cursor-pointer text-green-300 ${className}`}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
            />
          ) : (
            <AlertCircle
              className={`text-destructive absolute top-1/2 right-4 h-4 w-4 -translate-y-1/2 cursor-pointer ${className}`}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
            />
          )
        ) : null}
      </PopoverTrigger>

      {hasErrors && (
        <PopoverContent
          side="top"
          align="end"
          sideOffset={sideOffset}
          className={cn(
            getBackgroundClass(),
            "relative mb-5 max-w-48 p-2.5",
            bgVariant === "accent" ? "border-input border" : "border-0",
            className
          )}
          onOpenAutoFocus={(e) => {
            // Prevent auto-focus on popover content
            e.preventDefault();
            // Return focus to input
            if (
              triggerRef &&
              typeof triggerRef === "object" &&
              "current" in triggerRef &&
              triggerRef.current
            ) {
              triggerRef.current.focus();
            }
          }}
        >
          {/* Arrow pointing down to input */}
          <div
            className={cn(
              getArrowBorderClass(),
              "absolute right-4 -bottom-2 h-0 w-0 transform border-t-9 border-r-9 border-l-9 border-transparent"
            )}
          ></div>

          {/* Border arrow for accent variant */}
          {bgVariant === "accent" && (
            <div className="border-t-input absolute right-4 -bottom-[9px] h-0 w-0 transform border-t-8 border-r-8 border-l-8 border-transparent"></div>
          )}

          <ul className="space-y-1 text-xs">
            {errors.map((errorItem, index) => (
              <li key={index} className="flex items-start gap-2">
                {errorItem.isValidated ? (
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-300" />
                ) : (
                  <AlertCircle className="text-destructive mt-0.5 h-4 w-4 flex-shrink-0" />
                )}
                <span className={errorItem.isValidated ? "text-white" : ""}>
                  {errorItem.message}
                </span>
              </li>
            ))}
          </ul>
        </PopoverContent>
      )}
    </Popover>
  );
}
